import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { t } from "@/utils/locale";

const DAY_NAMES = ["", "Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const CHART_COLORS = ["#0d9488", "#6366f1", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"];

const CustomTooltip = ({ active, payload, label, prefix = "" }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs text-gray-500 font-medium">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-bold" style={{ color: p.color }}>
          {p.name}: {prefix}{typeof p.value === "number" ? p.value.toFixed(p.value % 1 ? 2 : 0) : p.value}
        </p>
      ))}
    </div>
  );
};

export default function DashboardCharts({ analytics, canSeeFinancials }) {
  const dailyData = (analytics?.daily_stats || []).map(d => ({
    date: d._id?.slice(5) || "", pedidos: d.orders, ingresos: Math.round(d.revenue),
  }));
  const hourlyData = Array.from({ length: 24 }, (_, i) => {
    const h = String(i).padStart(2, "0");
    const found = (analytics?.hourly_stats || []).find(s => s._id === h);
    return { hour: `${h}h`, pedidos: found?.orders || 0 };
  }).filter((_, i) => i >= 8 && i <= 23);
  const weekdayData = Array.from({ length: 7 }, (_, i) => {
    const dow = i + 1;
    const found = (analytics?.weekday_stats || []).find(s => s._id === dow);
    return { day: DAY_NAMES[dow], pedidos: found?.orders || 0 };
  });
  const avgTicketData = (analytics?.avg_ticket_stats || []).map(d => ({
    date: d._id?.slice(5) || "", ticket: Math.round(d.avg_ticket * 100) / 100,
  }));
  const topProducts = (analytics?.top_products || []).slice(0, 8).map((p, i) => ({
    name: p._id?.length > 18 ? p._id.slice(0, 18) + "..." : p._id,
    qty: p.quantity, revenue: Math.round(p.revenue), fill: CHART_COLORS[i % CHART_COLORS.length],
  }));

  return (
    <>
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="bg-card border-border/50">
          <CardContent className="pt-5">
            <h3 className="font-bold uppercase text-sm mb-4">{t("ordersPerDay")}</h3>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={dailyData}>
                <defs><linearGradient id="gradPedidos" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0d9488" stopOpacity={0.3} /><stop offset="95%" stopColor="#0d9488" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="date" tick={{ fill: "#9ca3af", fontSize: 10 }} />
                <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="pedidos" name="Pedidos" stroke="#0d9488" strokeWidth={2.5} fill="url(#gradPedidos)" dot={{ fill: "#0d9488", r: 3 }} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        {canSeeFinancials && (
          <Card className="bg-card border-border/50">
            <CardContent className="pt-5">
              <h3 className="font-bold uppercase text-sm mb-4">{t("revenuePerDay")}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={dailyData}>
                  <defs><linearGradient id="gradIngresos" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#16a34a" stopOpacity={0.3} /><stop offset="95%" stopColor="#16a34a" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="date" tick={{ fill: "#9ca3af", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip prefix="€" />} />
                  <Area type="monotone" dataKey="ingresos" name="Ingresos" stroke="#16a34a" strokeWidth={2.5} fill="url(#gradIngresos)" dot={{ fill: "#16a34a", r: 3 }} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="bg-card border-border/50" data-testid="hourly-chart">
          <CardContent className="pt-5">
            <h3 className="font-bold uppercase text-sm mb-4">Pedidos por hora</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="hour" tick={{ fill: "#9ca3af", fontSize: 9 }} />
                <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="pedidos" name="Pedidos" fill="#0d9488" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50" data-testid="weekday-chart">
          <CardContent className="pt-5">
            <h3 className="font-bold uppercase text-sm mb-4">Día de la semana</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={weekdayData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="day" tick={{ fill: "#9ca3af", fontSize: 11 }} />
                <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="pedidos" name="Pedidos" radius={[4, 4, 0, 0]}>
                  {weekdayData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {canSeeFinancials && (
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="bg-card border-border/50" data-testid="avg-ticket-chart">
            <CardContent className="pt-5">
              <h3 className="font-bold uppercase text-sm mb-4">Ticket medio por día</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={avgTicketData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="date" tick={{ fill: "#9ca3af", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip prefix="€" />} />
                  <Line type="monotone" dataKey="ticket" name="Ticket" stroke="#f59e0b" strokeWidth={2.5} dot={{ fill: "#f59e0b", r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50" data-testid="top-products-chart">
            <CardContent className="pt-5">
              <h3 className="font-bold uppercase text-sm mb-4">Top productos</h3>
              {topProducts.length > 0 ? (
                <div className="space-y-2.5">
                  {topProducts.map((p, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-4 text-right">{i + 1}</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-sm font-medium truncate max-w-[160px]">{p.name}</span>
                          <span className="text-xs text-muted-foreground">{p.qty} uds</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, (p.qty / (topProducts[0]?.qty || 1)) * 100)}%`, backgroundColor: p.fill }} />
                        </div>
                      </div>
                      <span className="text-xs font-bold text-emerald-600 w-16 text-right">€{p.revenue}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">Sin datos aún</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
